import { useContext, useMemo } from 'react'
import type {
  VirtualIpCharacterBatch,
  VirtualIpCharacterSelectionWorkspace,
  VirtualIpDraftStatus,
} from '../../api'
import {
  buildVirtualIpWorkflowSnapshot,
  resolveVirtualIpWorkflowProgress,
  type VirtualIpWorkflowProgress,
} from './virtualIpProgress'
import { VirtualIpWorkflowContext } from './VirtualIpWorkflowContext'

const EMPTY_PROGRESS: VirtualIpWorkflowProgress = {
  current_step: 1,
  highest_completed_step: 0,
  furthest_reached_step: 0,
  unlocked_steps: [1],
  completed_steps: [],
  step_states: { 1: 'AVAILABLE' },
  step_state_reasons: {},
  is_complete: false,
}

/** Canonical progress — always prefers shared workflow context when mounted
 * inside VirtualIpWorkflowProvider (never page-local partial server facts). */
export function useResolvedVirtualIpProgress(
  input?: {
    draftStatus?: VirtualIpDraftStatus | null | undefined
    selectionWorkspace?: VirtualIpCharacterSelectionWorkspace | null
    latestBatch?: VirtualIpCharacterBatch | null
    voiceConfirmed?: boolean
    workflowComplete?: boolean
  },
): VirtualIpWorkflowProgress {
  const ctx = useContext(VirtualIpWorkflowContext)

  const local = useMemo(
    () =>
      input
        ? resolveVirtualIpWorkflowProgress(
            buildVirtualIpWorkflowSnapshot({
              draftStatus: input.draftStatus ?? 'draft',
              selectionWorkspace: input.selectionWorkspace,
              latestBatch: input.latestBatch,
              voiceConfirmed: input.voiceConfirmed,
              workflowComplete: input.workflowComplete,
            }),
          )
        : EMPTY_PROGRESS,
    [
      input?.draftStatus,
      input?.selectionWorkspace,
      input?.latestBatch,
      input?.voiceConfirmed,
      input?.workflowComplete,
    ],
  )

  if (ctx) return ctx.progress
  return local
}
